import React, { useEffect, useState } from "react";
import { api, API, authedFetch, formatApiErrorDetail } from "../lib/api";
import { Plus, Trash2, X, Pencil, Eye, EyeOff, Copy, Check, Upload, Image as ImageIcon, Users as UsersIcon } from "lucide-react";
import LinkedUsersDialog from "../components/LinkedUsersDialog";

function LogoUploader({ value, onChange }) {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!value) { setPreview(null); return; }
    let revoked = false;
    let url = null;
    (async () => {
      try {
        const res = await authedFetch(`${API}/files/${value}`);
        if (!res.ok) return;
        const b = await res.blob();
        url = URL.createObjectURL(b);
        if (!revoked) setPreview(url);
      } catch { /* ignore */ }
    })();
    return () => { revoked = true; if (url) { try { URL.revokeObjectURL(url); } catch { /* */ } } };
  }, [value]);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setError("");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload fallito");
    }
    setUploading(false);
  };

  return (
    <div>
      <div className="flex items-center gap-3">
        <div className="w-20 h-20 border-[1.5px] border-black bg-white rounded-lg overflow-hidden flex items-center justify-center">
          {preview ? (
            <img src={preview} alt="Logo cliente" className="w-full h-full object-contain p-1" />
          ) : (
            <ImageIcon className="w-8 h-8 text-neutral-400" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <label className="bru-btn bru-btn-secondary cursor-pointer !px-3 !py-2 !text-xs" data-testid="client-logo-upload-label">
            <Upload className="w-4 h-4" />
            {uploading ? "Caricamento…" : value ? "Cambia" : "Carica logo"}
            <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="client-logo-file-input" />
          </label>
          {value && (
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-xs text-[#ff3b30] font-bold hover:underline"
              data-testid="client-logo-remove"
            >
              Rimuovi
            </button>
          )}
        </div>
      </div>
      {error && <div className="text-xs text-[#ff3b30] mt-1">{error}</div>}
      <div className="text-[10px] text-neutral-500 mt-1">PNG/JPG · max 8 MB</div>
    </div>
  );
}

function ClientDialog({ open, initial, onClose, onSaved }) {
  const isEdit = Boolean(initial);
  const [form, setForm] = useState({ name: "", email: "", password: "", logo: null });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: initial.name || "",
        email: initial.email || "",
        password: "",
        logo: initial.logo || null,
      });
    } else {
      setForm({ name: "", email: "", password: "", logo: null });
    }
    setError("");
    setShowPassword(false);
  }, [open, initial]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = {};
      if (!isEdit) {
        payload.name = form.name;
        payload.email = form.email;
        if (form.password) payload.password = form.password;
        if (form.logo) payload.logo = form.logo;
        await api.post("/clients", payload);
      } else {
        if (form.name !== initial.name) payload.name = form.name;
        if (form.email !== initial.email) payload.email = form.email;
        if (form.password) payload.password = form.password;
        if ((form.logo || null) !== (initial.logo || null)) payload.logo = form.logo;
        if (Object.keys(payload).length > 0) {
          await api.patch(`/clients/${initial.id}`, payload);
        }
      }
      setSaving(false);
      onSaved();
    } catch (err) {
      setSaving(false);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto" onClick={onClose} data-testid="client-dialog">
      <div className="bru-card w-full max-w-md p-6 mt-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="tag-label">{isEdit ? "MODIFICA" : "NUOVO"} · CLIENTE</div>
            <h3 className="text-2xl font-bold" >
              {isEdit ? "Modifica Cliente" : "Nuovo Cliente"}
            </h3>
          </div>
          <button onClick={onClose} className="bru-btn bru-btn-ghost !px-2 !py-1" data-testid="close-client-dialog">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="bru-label">Logo azienda (opzionale)</label>
            <LogoUploader value={form.logo} onChange={(v) => setForm({ ...form, logo: v })} />
          </div>
          <div>
            <label className="bru-label">Nome Azienda *</label>
            <input
              required
              className="bru-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="client-name-input"
            />
          </div>
          <div>
            <label className="bru-label">Email di accesso *</label>
            <input
              type="email"
              required
              className="bru-input"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              data-testid="client-email-input"
            />
          </div>
          <div>
            <label className="bru-label">{isEdit ? "Nuova Password (lascia vuoto per non cambiare)" : "Password iniziale"}</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                minLength={form.password ? 6 : 0}
                className="bru-input pr-10"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="client-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-500 hover:text-black"
                aria-label="Mostra/Nascondi password"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {!isEdit && (
              <div className="text-[10px] text-neutral-500 mt-1">
                Lascia vuoto se questa email è già usata da un altro cliente: verrà riutilizzata la password esistente e l&apos;utente potrà accedere a entrambi i clienti con le stesse credenziali.
              </div>
            )}
          </div>
          {error && (
            <div className="border-[1.5px] border-black bg-[#ff3b30] text-white px-3 py-2 text-sm rounded-md" data-testid="client-error">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary" data-testid="cancel-client-button">Annulla</button>
            <button type="submit" disabled={saving} className="bru-btn bru-btn-primary disabled:opacity-60" data-testid="submit-client-button">
              {saving ? "SALVATAGGIO…" : (isEdit ? "AGGIORNA" : "CREA CLIENTE")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PasswordCell({ password }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  if (!password) return <span className="text-xs text-neutral-400 italic">imposta una nuova password</span>;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="flex items-center gap-2">
      <code className="font-mono text-xs bg-neutral-100 px-2 py-1 rounded border border-neutral-300 select-all">
        {show ? password : "••••••••"}
      </code>
      <button onClick={() => setShow((s) => !s)} className="text-neutral-500 hover:text-black" title="Mostra/Nascondi">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
      <button onClick={copy} className="text-neutral-500 hover:text-[#ff5a00]" title="Copia">
        {copied ? <Check className="w-4 h-4 text-[#10b981]" /> : <Copy className="w-4 h-4" />}
      </button>
    </div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [linkedClient, setLinkedClient] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    reload();
  }, []);

  const doDelete = async () => {
    if (!confirm) return;
    await api.delete(`/clients/${confirm.id}`);
    setConfirm(null);
    reload();
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="clients-page">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
        <div>
          <h1 className="text-5xl lg:text-6xl font-bold tracking-tight leading-none" >
            Clienti <span className="text-[#ff5a00]">azienda</span>
          </h1>
        </div>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="bru-btn bru-btn-primary self-start" data-testid="add-client-button">
          <Plus className="w-4 h-4" />
          Nuovo Cliente
        </button>
      </div>

      <div className="bru-card overflow-hidden" data-testid="clients-list">
        <div className="hidden md:grid grid-cols-[1.2fr_1.4fr_1.4fr_0.6fr_280px] gap-4 px-5 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="tag-label">Nome Cliente</div>
          <div className="tag-label">Email</div>
          <div className="tag-label">Password</div>
          <div className="tag-label">DPI / Scaff.</div>
          <div className="tag-label text-right">Azioni</div>
        </div>
        {loading ? (
          <div className="p-8 text-center tag-label">CARICAMENTO…</div>
        ) : clients.length === 0 ? (
          <div className="p-10 text-center">
            <div className="tag-label mb-2">ARCHIVIO VUOTO</div>
            <h3 className="text-xl font-bold mb-2">Nessun cliente registrato</h3>
            <button onClick={() => { setEditing(null); setOpen(true); }} className="bru-btn bru-btn-primary mt-3" data-testid="empty-add-client-button">
              <Plus className="w-4 h-4" />
              Primo Cliente
            </button>
          </div>
        ) : (
          clients.map((c) => (
            <div
              key={c.id}
              className="grid grid-cols-1 md:grid-cols-[1.2fr_1.4fr_1.4fr_0.6fr_280px] gap-4 px-5 py-4 border-b border-neutral-100 last:border-b-0 items-center"
              data-testid={`client-row-${c.id}`}
            >
              <div>
                <div className="tag-label md:hidden">Nome</div>
                <div className="font-bold">{c.name}</div>
              </div>
              <div className="min-w-0">
                <div className="tag-label md:hidden">Email</div>
                <div className="font-mono text-sm truncate">{c.email}</div>
              </div>
              <div>
                <div className="tag-label md:hidden">Password</div>
                <PasswordCell password={c.password} />
              </div>
              <div>
                <div className="tag-label md:hidden">DPI / Scaff.</div>
                <div className="font-bold tabular-nums" data-testid={`client-counts-${c.id}`}>
                  {c.devices_count ?? 0} <span className="text-neutral-400 font-normal">/</span> {c.scaffolds_count ?? 0}
                </div>
              </div>
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => setLinkedClient(c)}
                  className="bru-btn bru-btn-secondary !px-3 !py-1.5 text-xs"
                  data-testid={`linked-users-${c.id}`}
                  title="Gestisci accessi multi-cliente"
                >
                  <UsersIcon className="w-4 h-4" />
                  Accessi
                </button>
                <button
                  onClick={() => { setEditing(c); setOpen(true); }}
                  className="bru-btn bru-btn-secondary !px-3 !py-1.5 text-xs"
                  data-testid={`edit-client-${c.id}`}
                >
                  <Pencil className="w-4 h-4" />
                  Modifica
                </button>
                <button
                  onClick={() => setConfirm(c)}
                  className="bru-btn bru-btn-secondary !text-[#ff3b30] !px-3 !py-1.5 text-xs"
                  data-testid={`delete-client-${c.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ClientDialog
        open={open}
        initial={editing}
        onClose={() => { setOpen(false); setEditing(null); }}
        onSaved={() => { setOpen(false); setEditing(null); reload(); }}
      />

      {linkedClient && (
        <LinkedUsersDialog client={linkedClient} onClose={() => setLinkedClient(null)} />
      )}

      {confirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-client-dialog">
          <div className="bru-card p-6 max-w-sm w-full">
            <div className="tag-label mb-1">CONFERMA</div>
            <h3 className="text-xl font-bold mb-3" >
              Eliminare <span className="text-[#ff5a00]">{confirm.name}</span>?
            </h3>
            <p className="text-sm text-neutral-600 mb-5">
              Verranno eliminati l&apos;account, tutti i DPI ({confirm.devices_count}) e tutte le scaffalature ({confirm.scaffolds_count ?? 0}) con le relative ispezioni e verifiche. Operazione non reversibile.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirm(null)} className="bru-btn bru-btn-secondary" data-testid="cancel-delete-client-button">Annulla</button>
              <button onClick={doDelete} className="bru-btn bru-btn-danger" data-testid="confirm-delete-client-button">Elimina Tutto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
